import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useRecoilValue } from 'recoil';
import { setRecoil } from 'recoil-nexus';

import { cancelReEmitFor, socketEmit } from 'lib/clients/socket';
import { useAiPromptTemplate } from 'lib/hooks/queries/admin/intelligence/useAiPromptTemplate';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import { withErrorBoundary } from 'components/shared/Error/Boundary';
import Modal from 'components/shared/Modal/Modal';
import Select from 'components/shared/Select/Select';
import Textarea from 'components/shared/Textarea/Textarea';
import state from 'components/state';

import { AiTaskValidationSchema } from '../lib/AiTaskValidationSchema';

const NewIntelligenceModelModal = ({ isOpen, onModalClose, promptOptions }) => {
  const newAiTaskState = useRecoilValue(state.intelligenceAiState);
  const queryClient = useQueryClient();
  const [clientId, setClientId] = useState(null);
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: {
      promptTemplate: null,
      promptTemplateVersion: null,
      body: ''
    },
    enableReinitialize: true,
    validationSchema: AiTaskValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      const generatedClientId = self.crypto.randomUUID();
      setClientId(generatedClientId);
      socketEmit('user-message', {
        kind: 'myscribe-ai',
        message: {
          promptTemplateId: values?.promptTemplate?.value,
          promptTemplateVersion: values?.promptTemplateVersion?.value,
          body: values?.body,
          kind: 'manual',
          clientId: generatedClientId
        }
      });
      setSubmitting(false);
    }
  });

  const { isLoading, isFetching, data } = useAiPromptTemplate({
    params: {
      id: formik?.values?.promptTemplate?.value
    },
    options: {
      enabled: !!formik?.values?.promptTemplate?.value
    },
    dependencies: [formik?.values?.promptTemplate?.value],
    name: `ai_prompt_template_${formik?.values?.promptTemplate?.value}`
  });

  useEffect(() => {
    socketEmit('user-join', { room: 'admin-myscribe-ai-task' }, { reemitOnReconnect: true });
    return () => {
      socketEmit('user-leave', { room: 'admin-myscribe-ai-task' });
      cancelReEmitFor('user-join', { room: 'admin-myscribe-ai-task' });
    };
  });

  const resetRecoilState = () => {
    setRecoil(state.intelligenceAiState, (ps) => ({
      ...ps,
      taskId: null,
      clientId: null,
      kind: null,
      vertical: null
    }));
    setClientId(null);
  };

  const invalidateAiQuery = async () => {
    await queryClient.invalidateQueries(['ai_tasks']);
    if (newAiTaskState?.taskId) {
      showAlert({ title: 'Ai Task Created Successfully', color: 'success' });
      resetRecoilState();
    }
    formik.resetForm();
    onModalClose();
  };

  useEffect(() => {
    if (clientId && newAiTaskState?.clientId === clientId) {
      navigate(`?aiTaskId=${newAiTaskState?.taskId}`);
      resetRecoilState();
    } else {
      invalidateAiQuery();
    }
  }, [newAiTaskState, clientId]);

  return (
    <Modal
      isOpen={isOpen}
      handleClose={() => {
        formik.resetForm();
        onModalClose();
      }}
      title="New AI Task"
      slideFromRight
      footer={
        <div className="flex w-full justify-between">
          <Button
            outlined
            color="neutral"
            text="Close"
            onClick={() => {
              formik.resetForm();
              onModalClose();
            }}
          />
          <Button outlined color="primary" text="Submit" onClick={formik.submitForm} />
        </div>
      }>
      <div className="grid grid-cols-2 gap-2">
        <Select
          label="Prompt Template"
          isClearable
          options={promptOptions?.map((p) => {
            return {
              label: `${p?.id}`,
              value: p?.id
            };
          })}
          onChange={(e) => {
            formik.setFieldValue('promptTemplate', e || null);
          }}
          value={formik.values.promptTemplate}
          placeholder="Select Prompt Template"
          error={formik.errors['promptTemplate']}
        />
        <Select
          label="Version"
          isClearable
          options={data?.result.map((v) => {
            return {
              label: `${v?.version}`,
              value: v?.version
            };
          })}
          onChange={(e) => {
            formik.setFieldValue('promptTemplateVersion', e || null);
          }}
          value={formik.values.promptTemplateVersion}
          placeholder="Select Version"
          error={formik.errors['promptTemplateVersion']}
          disabled={!formik.values.promptTemplate?.value || isLoading || isFetching}
        />
        <Textarea
          value={formik?.values?.body}
          resizeY={true}
          className="col-span-full"
          label="Prompt"
          error={formik.errors['body']}
          onChange={(e) => {
            formik.setFieldValue('body', e?.target?.value);
          }}
        />
      </div>
    </Modal>
  );
};

export default withErrorBoundary(NewIntelligenceModelModal);
